import type { ApiClient, Schemas } from '@argzombie/api-client';
import {
  isTesterToolsOff,
  kindParam,
  saveFileName,
  TesterToolsMissing,
  type DevSaveFile,
  type DevSaveRow,
  type KindFilter,
  type ResetMode,
} from './devSaves';
import { downloadBlob } from './triggerCodes';

/**
 * Save points (tester tools), all on the typed client. Every call throws `TesterToolsMissing` when the API answers
 * 404 `not_found` (no `Dev:TesterTools`), or a `DevSaveProblem` carrying the ProblemDetails `code` (`name_taken`...).
 */

/** An API problem as one line ("A save named x exists. (name_taken)"), keeping the status and code. */
export class DevSaveProblem extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | undefined,
  ) {
    super(message);
    this.name = 'DevSaveProblem';
  }
}

type Result<T> = { data?: T; error?: unknown; response: Response };

function check(r: Result<unknown>): void {
  if (r.response.ok) return;
  if (isTesterToolsOff(r.response.status, r.error)) throw new TesterToolsMissing();
  const p = (r.error ?? {}) as { detail?: string; title?: string; code?: string };
  const message = p.detail ?? p.title ?? `HTTP ${r.response.status}`;
  throw new DevSaveProblem(
    p.code && p.code !== message ? `${message} (${p.code})` : message,
    r.response.status,
    p.code,
  );
}

function body<T>(r: Result<T>): T {
  check(r);
  if (r.data === undefined)
    throw new DevSaveProblem('The API sent no body.', r.response.status, undefined);
  return r.data;
}

/** True when an error is the API telling us `name_taken` (resend with overwrite after a confirm). */
export const isNameTaken = (e: unknown) => e instanceof DevSaveProblem && e.code === 'name_taken';

/** Rows the list fetches at once (the API caps `take` at 500). */
export const LIST_TAKE = 500;

export const listSaves = async (
  client: ApiClient,
  f: { q?: string; playerId?: string; kind: KindFilter },
) =>
  body(
    await client.GET('/dev/saves', {
      params: {
        query: {
          q: f.q?.trim() || undefined,
          playerId: f.playerId,
          kind: kindParam(f.kind),
          take: LIST_TAKE,
        },
      },
    }),
  );

export const createSave = async (
  client: ApiClient,
  req: { playerId: string; name: string; description?: string | null; overwrite: boolean },
) => body(await client.POST('/dev/saves', { body: req }));

export const deleteSave = async (client: ApiClient, id: string) =>
  check(await client.DELETE('/dev/saves/{id}', { params: { path: { id } } }));

/** Fetches the export with auth and saves it as `<slug>.save.json`. */
export async function exportSave(client: ApiClient, save: Pick<DevSaveRow, 'id' | 'name'>) {
  const r = await client.GET('/dev/saves/{id}/export', {
    params: { path: { id: save.id } },
    parseAs: 'blob',
  });
  check(r);
  downloadBlob(r.data as Blob, saveFileName(save.name));
}

export const importSave = async (
  client: ApiClient,
  req: { file: DevSaveFile; name?: string | null; overwrite: boolean },
) => body(await client.POST('/dev/saves/import', { body: req }));

export const loadSave = async (client: ApiClient, saveId: string, playerId: string) =>
  body(
    await client.POST('/dev/saves/{id}/load', {
      params: { path: { id: saveId } },
      body: { playerId },
    }),
  );

export const resetPlayer = async (
  client: ApiClient,
  playerId: string,
  mode: ResetMode,
) =>
  body(
    await client.POST('/dev/players/{id}/reset', {
      params: { path: { id: playerId } },
      body: { mode },
    }),
  );

export const undoPlayer = async (client: ApiClient, playerId: string) =>
  body(await client.POST('/dev/players/{id}/undo', { params: { path: { id: playerId } } }));

export const findPlayers = async (
  client: ApiClient,
  q: string,
): Promise<Schemas['DevSavePlayerMatch'][]> =>
  body(await client.GET('/dev/saves/players', { params: { query: { q } } }));

export const saveGraph = async (client: ApiClient) => body(await client.GET('/dev/saves/graph'));
